import { useState, useRef } from 'react';
import toast from 'react-hot-toast';

export interface UseBulkUploadReturn {
  bulkInputText: string;
  setBulkInputText: React.Dispatch<React.SetStateAction<string>>;
  temperatureInputText: string;
  setTemperatureInputText: React.Dispatch<React.SetStateAction<string>>;
  bulkInputValidation: {
    isValid: boolean;
    missingFields: string[];
    message: string;
  };
  setBulkInputValidation: React.Dispatch<React.SetStateAction<{
    isValid: boolean;
    missingFields: string[];
    message: string;
  }>>;
  equipmentCalibrationCertificateNo: string;
  setEquipmentCalibrationCertificateNo: React.Dispatch<React.SetStateAction<string>>;
  selectedFiles: File[];
  setSelectedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  isUploading: boolean;
  setIsUploading: React.Dispatch<React.SetStateAction<boolean>>;
  uploadProgress: number;
  setUploadProgress: React.Dispatch<React.SetStateAction<number>>;
  csvPreviewData: any[];
  setCsvPreviewData: React.Dispatch<React.SetStateAction<any[]>>;
  csvColumnOrder: string[];
  setCsvColumnOrder: React.Dispatch<React.SetStateAction<string[]>>;
  showPreview: boolean;
  setShowPreview: React.Dispatch<React.SetStateAction<boolean>>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  handleFileSelect: (e: React.ChangeEvent<HTMLInputElement>) => Promise<void>;
  clearFiles: () => void;
}

export const useBulkUpload = (): UseBulkUploadReturn => {
  const [bulkInputText, setBulkInputText] = useState("");
  const [temperatureInputText, setTemperatureInputText] = useState("");
  const [bulkInputValidation, setBulkInputValidation] = useState<{
    isValid: boolean;
    missingFields: string[];
    message: string;
  }>({
    isValid: true,
    missingFields: [],
    message: "",
  });
  const [equipmentCalibrationCertificateNo, setEquipmentCalibrationCertificateNo] = useState("");
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [csvPreviewData, setCsvPreviewData] = useState<any[]>([]);
  const [csvColumnOrder, setCsvColumnOrder] = useState<string[]>([]);
  const [showPreview, setShowPreview] = useState(true);

  const parseCSVLine = (line: string): string[] => {
    const result: string[] = [];
    let current = '';
    let insideQuotes = false;

    for (let i = 0; i < line.length; i++) {
      const char = line[i];
      if (char === '"') {
        insideQuotes = !insideQuotes;
      } else if (char === ',' && !insideQuotes) {
        result.push(current.trim().replace(/^"|"$/g, ''));
        current = '';
      } else {
        current += char;
      }
    }
    result.push(current.trim().replace(/^"|"$/g, ''));
    return result;
  };

  const handleFileSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    setSelectedFiles(files);
    
    // Parse CSV file for preview
    if (files.length > 0) {
      const file = files[0];
      setIsUploading(true);
      try {
        const text = await file.text();
        const lines = text.split('\n').filter(line => line.trim());
        
        if (lines.length > 1) {
          const headerLine = lines[0];
          const headers = parseCSVLine(headerLine);
          
          const data = lines.slice(1).map((line, index) => {
            const values = parseCSVLine(line);
            const row: any = { rowNumber: index + 1 };
            
            headers.forEach((header, colIndex) => {
              row[header] = values[colIndex] || '';
            });
            
            return row;
          });
          
          setCsvPreviewData(data);
          setCsvColumnOrder(headers);
          setShowPreview(true);
          toast.success(`CSV file parsed successfully! Found ${data.length} rows.`);
        }
      } catch (error) {
        toast.error('Failed to parse CSV file');
      } finally {
        setIsUploading(false);
      }
    } else {
      setCsvPreviewData([]);
      setCsvColumnOrder([]);
    }
  };

  const clearFiles = () => {
    setSelectedFiles([]);
    setCsvPreviewData([]);
    setCsvColumnOrder([]);
    setEquipmentCalibrationCertificateNo("");
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return {
    bulkInputText,
    setBulkInputText,
    temperatureInputText,
    setTemperatureInputText,
    bulkInputValidation,
    setBulkInputValidation,
    equipmentCalibrationCertificateNo,
    setEquipmentCalibrationCertificateNo,
    selectedFiles,
    setSelectedFiles,
    isUploading,
    setIsUploading,
    uploadProgress,
    setUploadProgress,
    csvPreviewData,
    setCsvPreviewData,
    csvColumnOrder,
    setCsvColumnOrder,
    showPreview,
    setShowPreview,
    fileInputRef,
    handleFileSelect,
    clearFiles,
  };
};

